import styled, { css } from 'styled-components';

export type ChipTone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';

const chipTones: Record<ChipTone, { bg: string; fg: string }> = {
  ok: { bg: '#e3f4ea', fg: '#23764a' },
  warn: { bg: '#fdf3d6', fg: '#8a6408' },
  bad: { bg: '#fbecef', fg: '#a33a4e' },
  info: { bg: '#e6f3fa', fg: '#0070a6' },
  mute: { bg: '#ebebeb', fg: '#696969' },
};

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  overflow-y: auto;
  padding-bottom: 24px;
`;

export const Card = styled.section<{ $dashed?: boolean }>`
  background: #fff;
  border: 1px ${({ $dashed }) => ($dashed ? 'dashed #0090d3' : 'solid #ebebeb')};
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #696969;
`;

export const Chip = styled.span<{ $tone: ChipTone }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  font-size: 11px;
  font-weight: 700;
  border-radius: 100px;
  padding: 2px 9px;
  white-space: nowrap;
  background: ${({ $tone }) => chipTones[$tone].bg};
  color: ${({ $tone }) => chipTones[$tone].fg};
`;

export const PillButton = styled.button<{ $variant?: 'primary' | 'outline' | 'danger' }>`
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 700;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  ${({ $variant = 'outline' }) =>
    $variant === 'primary'
      ? css`
          background: #0090d3;
          color: #fff;
          border: 1px solid #0090d3;
          &:hover:not(:disabled) {
            background: #007bb4;
          }
        `
      : $variant === 'danger'
        ? css`
            background: #fff;
            color: #a33a4e;
            border: 1px solid #e6b8c2;
            &:hover:not(:disabled) {
              background: #fbecef;
            }
          `
        : css`
            background: #fff;
            color: #4a4a4a;
            border: 1px solid #d9d9d9;
            &:hover:not(:disabled) {
              border-color: #0090d3;
              color: #0090d3;
            }
          `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Banner = styled.div<{ $tone: ChipTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  background: ${({ $tone }) => chipTones[$tone].bg};
  color: ${({ $tone }) => chipTones[$tone].fg};
`;

export const MutedText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #696969;
  line-height: 1.45;
`;

export const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
`;

export const TextInput = styled.input`
  height: 2.4rem;
  padding: 0 14px;
  border-radius: 2rem;
  border: 1px solid transparent;
  background: #f1f1f1;
  font-size: 13px;
  font-family: inherit;
  color: #2b2b2b;
  outline: none;

  &:focus {
    border-color: #0090d3;
    background: #fff;
  }

  &:disabled,
  &[readonly] {
    color: #696969;
    cursor: default;
  }
`;

export const FieldError = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #a33a4e;
`;

export const ListPanel = styled(Card)`
  position: sticky;
  top: 0;
  gap: 10px;
`;

export const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const EmitterItem = styled.button<{ $active: boolean; $inactive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  background: ${({ $active }) => ($active ? '#e6f3fa' : '#fff')};
  border: ${({ $active }) => ($active ? '2px solid #0090d3' : '1px solid #e2e2e2')};
  opacity: ${({ $inactive }) => ($inactive ? 0.55 : 1)};

  &:hover {
    border-color: #0090d3;
  }
`;

export const EmitterName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #2b2b2b;
  line-height: 1.3;
`;

export const EmitterCnpj = styled.span`
  font-size: 12px;
  color: #696969;
  font-variant-numeric: tabular-nums;
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const EmptyState = styled.div`
  padding: 32px 20px;
  text-align: center;
  font-size: 13px;
  color: #696969;
`;

export const Overlay = styled.div<{ $align?: 'center' | 'right' }>`
  position: fixed;
  inset: 0;
  z-index: 9002;
  display: flex;
  align-items: ${({ $align }) => ($align === 'right' ? 'stretch' : 'center')};
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'center')};
  background: rgba(0, 0, 0, 0.35);
  font-family: 'Inter', sans-serif;
`;

export const DialogTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #2b2b2b;
`;

export const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

export const Dialog = styled.form`
  width: min(440px, calc(100vw - 32px));
  background: #fff;
  border-radius: 16px;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;
